/* FILE: ./frontend/src/features/canada/suppliers/SupplierCard.jsx */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const SupplierCard = ({ supplier, showEditButton, onContact }) => {
  const { t } = useTranslation();
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);

  if (!supplier) return null;

  // ⚡ ARRAY SAFETY: Ensure categories and capabilities don't crash the UI
  const categories = Array.isArray(supplier.categories) ? supplier.categories : [];
  const capabilities = Array.isArray(supplier.capabilities) ? supplier.capabilities : 
                       (typeof supplier.capabilities === 'string' ? [supplier.capabilities] : []);

  // --- ⚡ STRICT 100% BLIND DIRECTORY RULE ---
  // Only the Owner of the supplier profile and Platform Admins see the unmasked company name in the Directory.
  // All Buyers (Free & Pro) and other Suppliers see "Verified Supplier #XXXXXX" until a quote is accepted.
  const isOwner = user && supplier.user_id && String(user.id) === String(supplier.user_id);
  const isAdmin = userProfile?.role === 'admin';
  const isBlindIdentity = !isOwner && !isAdmin;

  const maskedId = supplier.id ? String(supplier.id).substring(0, 6).toUpperCase() : 'UNKNOWN';
  const displayName = isBlindIdentity
    ? (supplier.company_name?.startsWith('Verified Supplier') ? supplier.company_name : `Verified Supplier #${maskedId}`)
    : supplier.company_name;

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/supplier-onboarding', { state: { returnTo: '/directory' } }); 
  };

  // ⚡ FIXED: No longer pops up a false "Upgrade your account to view supplier identities" alert.
  // Logged-in users can view the blind profile (capabilities, certifications, and parts catalog).
  const handleViewDetails = () => {
    if (!user) {
        if (window.confirm(t('supplier_card.confirm_view_details', 'Login to view full supplier capabilities and parts catalog?'))) {
            navigate('/login');
        }
        return;
    }
    navigate(`/suppliers/${supplier.id}`);
  };

  // ⚡ OPTION A FOR RFQ: Routes directly to /rfq/new with preselectedSupplier
  const handleRequestQuote = (e) => {
    e.stopPropagation();
    if (isOwner) return;

    if (!user) {
        if (window.confirm(t('supplier_card.login_to_quote_confirm', 'You must be logged in to Request a Quote. Proceed to Login?'))) {
          navigate('/login');
        }
        return;
    }

    if (onContact) {
      onContact({ ...supplier, company_name: displayName });
    } else {
      navigate('/rfq/new', {
        state: {
          preselectedSupplier: { ...supplier, company_name: displayName }
        }
      });
    }
  };

  const getYoutubeEmbed = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
  };

  const getShortTier = (tier) => {
      if (!tier) return t('supplier_card.unverified_tier');
      return tier.split('(')[0].trim();
  };

  return (
    <div 
        className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all flex flex-col h-full cursor-pointer group"
        onClick={handleViewDetails}
        // ⚡ A11Y FIX: Inform screen readers this entire block is clickable
        role="button"
        tabIndex="0"
        aria-label={`View details for ${displayName}`}
        onKeyDown={(e) => { if(e.key === 'Enter') handleViewDetails(); }}
    >
      {/* HEADER */}
      <div className="p-6 pb-2">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              {supplier.logo_url && !isBlindIdentity ? (
                  // ⚡ SEO & A11Y FIX: Highly descriptive alt tag
                  <img 
                    src={supplier.logo_url} 
                    alt={`${displayName} corporate logo`} 
                    className="w-12 h-12 rounded object-contain border border-gray-100"
                  />
                ) : (
                 <div 
                    className={`w-12 h-12 rounded flex items-center justify-center text-white font-bold text-lg ${isBlindIdentity ? 'bg-slate-600' : 'bg-blue-600'}`}
                    aria-hidden="true"
                 >
                    {isBlindIdentity ? 'V' : (supplier.company_name?.charAt(0) || '?')}
                 </div>
                )}
            </div>
            
            <div className="flex-1 min-w-0"> 
              <h3 className="font-bold text-lg text-gray-900 line-clamp-1 group-hover:text-blue-600 transition-colors" data-testid="supplier-name">
                {isAdmin && <span className="text-red-500 mr-1" title="Visible to Admin">👮</span>}
                {displayName}
              </h3>
               <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 mt-1">
                 <span className="truncate max-w-[120px]">{supplier.city}, {supplier.province}</span>
                 {supplier.is_verified && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center font-bold whitespace-nowrap border bg-green-100 text-green-800 border-green-200">
                    ✓ {t('supplier_card.verified_badge')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="px-6 flex-grow flex flex-col">
        <p className="text-gray-600 mb-4 line-clamp-2 text-sm">
          {supplier.description || t('supplier_card.no_desc')}
        </p>
        
        {/* ⚡ MASTERFORMAT CATEGORIES */}
        {categories.length > 0 && (
            <div className="mb-3">
                <div className="flex flex-wrap gap-1.5">
                    {categories.map(cat => (
                        <span key={cat} className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100">
                            {cat}
                        </span>
                    ))}
                </div>
            </div>
        )}

        {/* ⚡ STANDARDIZED CAPABILITIES */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {capabilities.slice(0, 3).map((cap, idx) => (
            <span key={idx} className="bg-gray-50 text-gray-600 border border-gray-200 px-2 py-1 rounded text-[11px] font-medium">
              {cap}
            </span>
          ))}
          {capabilities.length > 3 && (
            <span className="text-gray-400 text-[11px] py-1 font-medium">
              {t('supplier_card.more', { count: capabilities.length - 3 })}
            </span>
          )}
        </div>

        {supplier.video_url && !isBlindIdentity && (
             <div className="mb-4" onClick={(e) => e.stopPropagation()}>
              <button 
                onClick={() => setShowVideo(!showVideo)}
                className="text-blue-600 hover:text-blue-800 text-xs font-bold flex items-center uppercase tracking-wide"
                aria-label={showVideo ? "Close facility tour video" : "Watch facility tour video"}
                tabIndex="-1"
               >
                <span className="mr-1 text-lg" aria-hidden="true">{showVideo ? '×' : '▸'}</span> 
                {showVideo ? t('supplier_card.close_video') : t('supplier_card.watch_tour')}
              </button>
              {showVideo && (
                <div className="mt-3 w-full aspect-video bg-black rounded-lg overflow-hidden shadow-lg">
                   <iframe 
                    src={getYoutubeEmbed(supplier.video_url)} 
                    className="w-full h-full" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                    title={`Facility Tour for ${displayName}`}
                  ></iframe>
                </div>
               )}
             </div>
        )}

        {/* ⚡ HOISTED CATALOG PRICING */}
        {supplier.catalogPrice && (
            <div className="mt-auto mb-2 pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Catalog Pricing</span>
                <span className="text-sm font-extrabold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
                    From ${supplier.catalogPrice}
                </span>
            </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between rounded-b-xl mt-4">
         <div className="text-xs shrink-0">
            {supplier.compliance_tier ? (
              <span 
                title={supplier.compliance_tier}
                className={`px-2 py-1 rounded border font-bold whitespace-nowrap ${
                supplier.compliance_tier.includes('Tier 1') ? 'bg-blue-50 text-blue-700 border-blue-200' :
                supplier.compliance_tier.includes('Tier 2') ? 'bg-amber-50 text-amber-700 border-amber-200' :
                'bg-gray-100 text-gray-600 border-gray-200'
              }`}>
                 {getShortTier(supplier.compliance_tier)}
              </span>
            ) : (
              <span className="text-gray-400 italic">{t('supplier_card.unverified_tier')}</span>
            )}
         </div>

         <div className="flex items-center space-x-2 ml-2">
            <button 
                onClick={(e) => { e.stopPropagation(); handleViewDetails(); }}
                className="text-blue-600 hover:text-blue-800 font-bold text-sm whitespace-nowrap"
                tabIndex="-1"
            >
                {t('supplier_card.view_details')}
            </button>

            {(showEditButton || isOwner) && (
              <button
                onClick={handleEdit}
                className="text-gray-500 hover:text-gray-900 text-xs font-bold uppercase tracking-wide ml-2"
                aria-label={`Edit business profile for ${displayName}`}
              >
                {t('supplier_card.edit_biz')}
              </button>
            )}
            
            {!isOwner && (
              <button
                onClick={handleRequestQuote}
                className="ml-2 px-3 py-2 rounded-lg text-xs font-bold shadow-sm transition-all whitespace-nowrap bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md"
                aria-label={`Request quote from ${displayName}`}
              >
                {t('supplier_card.req_quote')}
              </button>
            )}
         </div>
      </div>
    </div>
  );
};

export default SupplierCard;